import { XButtonBackground } from 'components/Chat/assets'
import { MessagingInput } from 'components/Chat/components'
import type { StreamChatGenerics } from 'components/Chat/types'
import SelectField from 'components/Common/SelectField'
import { CrossIcon } from 'components/Common/SvgIcons'
import PlusIcon from 'components/Common/SvgIcons/PlusIcon'
import { USER_ROLES } from 'config'
import { isEmpty } from 'lodash'
import _debounce from 'lodash.debounce'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import type {
  APIResponse,
  Channel,
  ChannelData,
  ExtendableGenerics,
  Message,
  UserResponse,
} from 'stream-chat'
import { MessageInput, MessageToSend, Thread, useChatContext, Window } from 'stream-chat-react'
import { DefaultStreamChatGenerics } from 'stream-chat-react/dist/types/types'
// import redirectTo from 'utils/redirectTo'
import './CreateChannel.css'
import { useTenantId } from 'utils/useTenantId'
import { ampli } from 'ampli'
import { getFileSizeFromUrl, guessImageFileTypeFromFileName } from 'utils/file-utils'
import { useUserInfo } from 'hooks/useUserInfo'
import { getChatRecipientData } from 'utils/helper'
import { useGetBusinessProfile, IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { RootState } from 'App'
import { useSelector } from 'react-redux'
import { useGetAdvisorDashboardQuery } from '__generated__/api-types-and-hooks'
import moment from 'moment'

const filterChatUsersByRole = ({ allUsers, chatUsers }) => {
  try {
    let chatUsersList: any[] = []
    let { glRole } = chatUsers

    // skip those users in which glRoles key is not available
    allUsers = allUsers.filter((user) => {
      if (user?.glRoles) {
        return user
      } else {
        return false
      }
    })

    if (glRole === USER_ROLES.BUSINESS_OWNER) {
      if (chatUsers?.users && chatUsers?.users.length > 0) {
        chatUsers?.users.forEach((chatUser) => {
          allUsers.find((user) => {
            if (user.id === chatUser.id) {
              chatUsersList.push(user)
              return true
            } else {
              return false
            }
          })
        })
      }
    } else if (glRole === USER_ROLES.BSO_ADMIN) {
      allUsers.forEach((user) => {
        if (user?.glRoles) {
          chatUsersList.push(user)
        }
      })
    } else if (glRole === USER_ROLES.BSO_ADVISOR) {
      if (chatUsers?.users && chatUsers?.users.length > 0) {
        chatUsers?.users.forEach((chatUser) => {
          allUsers.some((user: any) => {
            if (user.id === chatUser.id) {
              chatUsersList.push(user)
              return true
            }
            return false
          })
        })
      }
      // add advisors & admins in array
      allUsers.forEach((user: any) => {
        if (
          // user.glRoles.includes(USER_ROLES.BSO_ADMIN) ||
          user.glRoles.includes(USER_ROLES.BSO_ADVISOR)
        ) {
          chatUsersList.push(user)
        }
      })
    }

    return chatUsersList
  } catch (err: any) {
    console.log('error: filterChatUsersByRole: ', err.message)
    return []
  }
}

export type ChannelInnerProps = {
  toggleMobile: () => void
  theme: string
  showChannelPreview: any
  showChannelCreate: any
  setShowDesktopUserProfile: (showDesktopUserProfile) => void
  setShowChannelPreview: (showChannelPreview) => void
  showDesktopUserProfile
}

export type IStreamChatUserContext = DefaultStreamChatGenerics &
  ExtendableGenerics & {
    users: {
      firstName: string
      lastName: string
    }
  }

export type IStreamChannelContext = DefaultStreamChatGenerics &
  Channel<IStreamChatUserContext> & {
    members: (string | undefined)[]
    team: string | undefined
  }

export interface ICreateChannelProps {
  onClose: () => void
  setShowChannelPreview: (showChannelPreview: boolean) => void
  showChannelPreview: boolean
  setShowChannelCreate: (showChannelCreate: boolean) => void
  showChannelCreate: boolean
  chatUsers: any
  initialUserIds?: string[]
  setActiveMemberChannelExists: (activeMember: boolean) => void
}

const CreateChannel: FC<ICreateChannelProps> = ({
  onClose,
  setShowChannelPreview,
  showChannelPreview,
  setShowChannelCreate,
  showChannelCreate,
  chatUsers,
  initialUserIds,
  setActiveMemberChannelExists,
}) => {
  const { client } = useChatContext<IStreamChatUserContext>()

  const [searching, setSearching] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<UserResponse<StreamChatGenerics>[]>([])
  const [users, setUsers] = useState<UserResponse<IStreamChatUserContext>[]>([])
  const [showSelect, setShowSelect] = useState<boolean>(false)
  const [loadedInitialUsers, setLoadedInitialUsers] = useState<boolean>(false)
  const { userName, userId, roles, organizationName } = useUserInfo()
  const inputRef = useRef<HTMLInputElement>(null)
  const loggedInUser = useSelector((state: RootState) => state.user.user)
  const tenantId = useTenantId()
  const isAdvisor = loggedInUser?.roles?.includes(USER_ROLES.BSO_ADVISOR)
  // Get business profile to access team members
  let assigneeOptions: IAssigneeOptions[] = []
  const { data: advisorData } = useGetAdvisorDashboardQuery({ tenantId })
  const { assigneeOptions: businessAssigneeOptions } = useGetBusinessProfile({ enabled: true })
  if (!isAdvisor) {
    assigneeOptions = businessAssigneeOptions
  } else {
    const response = JSON.parse(advisorData?.getAdvisorDashboard?.data || '{}')
    assigneeOptions =
      response?.accounts?.map((account: any) => ({
        label: `${account?.firstName} ${account?.lastName}`,
        value: account?.id,
        user: {
          id: account?.id,
          firstName: account?.firstName,
          lastName: account?.lastName,
        },
      })) || []
  }
  assigneeOptions = assigneeOptions.filter((option) => option.value !== loggedInUser?.id)

  useEffect(() => {
    if (chatUsers) {
      findUsersDebounce()
    }
    return () => setActiveMemberChannelExists(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatUsers, users])

  const getAllUsers = async () => {
    const limit = 100
    let allFound = false
    let offset = 0
    let result: UserResponse<IStreamChatUserContext>[] = []
    let lastCreatedAt: string | null = moment().toISOString()
    let response: APIResponse & { users: Array<UserResponse<StreamChatGenerics>> }
    let offsetLimitReached = false

    while (!allFound) {
      if (offset >= 1000) {
        // https://support.getstream.io/hc/en-us/articles/4414446580119-Querying-for-users-channels-past-the-offset-1000-limit-Chat
        offsetLimitReached = true
        offset = 0
      }
      if (!offsetLimitReached) {
        response = await client.queryUsers(
          {
            id: { $ne: client.userID as string },
            teams: { $contains: tenantId },
          },
          { created_at: -1 },
          { limit, offset }
        )
      } else {
        response = await client.queryUsers(
          {
            id: { $ne: client.userID as string },
            teams: { $contains: tenantId },
            created_at: { $lt: lastCreatedAt },
          },
          { created_at: -1 },
          { limit, offset }
        )
      }
      if (response.users.length === 0) {
        allFound = true
      } else {
        result = result.concat(response.users)
        offset += limit
        lastCreatedAt = response.users[response.users.length - 1].created_at || null
      }
    }
    return result
  }

  const findUsers = async () => {
    if (searching) return
    setSearching(true)

    try {
      const users = await getAllUsers()

      let filterUser = users.filter((user) => user.firstName || user.lastName)

      let chatUsersList = filterChatUsersByRole({
        allUsers: filterUser,
        chatUsers,
      })

      chatUsersList.forEach((user) => {
        user.name = {
          firstName: user?.firstName || user?.name?.firstName,
          lastName: user?.lastName || user?.name?.lastName,
        }
      })

      if (chatUsersList.length) {
        chatUsersList = chatUsersList.sort((a, b) =>
          (a.firstName || '').localeCompare(b.firstName || '')
        )

        setUsers(chatUsersList)
      }
    } catch (error) {
      console.log({ error })
    }

    setSearching(false)
  }

  const findUsersDebounce = _debounce(findUsers, 100, {
    trailing: true,
  })

  const removeUser = (user: UserResponse<StreamChatGenerics>) => {
    const newUsers = selectedUsers.filter((item) => item.id !== user.id)
    setSelectedUsers(newUsers)
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const addUser = useCallback(
    (addedUser: UserResponse<StreamChatGenerics>) => {
      const isAlreadyAdded = selectedUsers.find((user) => user.id === addedUser.id)
      if (isAlreadyAdded) return

      setSelectedUsers([...selectedUsers, addedUser])
      if (inputRef.current) {
        inputRef.current.focus()
      }
    },
    [selectedUsers]
  )

  useEffect(() => {
    if (!loadedInitialUsers && users.length > 0) {
      for (const userId of initialUserIds || []) {
        const user = users.find((user) => user.id === userId) as UserResponse<StreamChatGenerics>
        if (user) addUser(user)

        setLoadedInitialUsers(true)
      }
    }
  }, [initialUserIds, addUser, users, tenantId, loadedInitialUsers])

  // const isNewConversation = (conversation: Channel<IStreamChatUserContext>) => {
  //   return conversation.state.last_message_at === null
  // }

  const createChannelHandler = async (message: MessageToSend<StreamChatGenerics>) => {
    try {
      const selectedUsersIds = selectedUsers.map((u) => u.id)

      const customData: ChannelData<IStreamChannelContext> = {
        members: [...selectedUsersIds, client.userID || 'unknown'],
        team: tenantId,
      }

      // Create channel
      const conversation = client.channel('messaging', customData)
      await conversation.create()

      // Explicitly wait for channel watch to be initialized before proceeding
      try {
        await conversation.watch()
      } catch (watchError: any) {
        console.error('Failed to watch channel:', watchError)
        throw new Error(`Channel initialization failed: ${watchError.message}`)
      }

      // Send message only after channel is fully initialized
      let newMessage: Message<IStreamChatUserContext> = {}
      if (!isEmpty(message.text)) {
        newMessage.text = message.text
      }
      if (message.attachments) {
        newMessage.attachments = []
      }
      for (const attachment of Object.values(message.attachments || {})) {
        let fileSize
        let fileType
        if (attachment.type === 'file') {
          fileSize = attachment.file_size || 0
          fileType = attachment.mime_type
        } else {
          fileSize = await getFileSizeFromUrl(attachment.image_url!)
          fileType = guessImageFileTypeFromFileName(attachment.fallback)
        }
        newMessage?.attachments?.push({
          ...attachment,
          file_size: fileSize,
          fileType,
        })
      }
      if (!isEmpty(newMessage)) {
        const recipientData = getChatRecipientData(conversation.state.members, userId)
        await conversation.sendMessage(newMessage)
        ampli.messageSent({
          senderId: userId,
          content: newMessage.text || '',
          senderName: userName,
          senderRoles: roles,
          recipientIds: recipientData.recipientIds,
          recipientNames: recipientData.recipientNames,
          recipientRoles: recipientData.recipientRoles,
          fileType: newMessage.attachments
            ? newMessage.attachments.map((fileInfo) => fileInfo.fileType)
            : [],
          fileSize: newMessage.attachments
            ? newMessage.attachments.map((fileInfo) => fileInfo.file_size)
            : [],
          organizationName,
        })
      }

      setShowChannelPreview(true)
      setSelectedUsers([])
      setUsers([])
      onClose()
    } catch (err: any) {
      console.error('Error creating channel:', err)
      // You might want to show a user-facing error message here
    }
  }

  // Convert assigneeOptions to StreamChat user format
  const convertAssigneeToStreamUser = (
    assignee: IAssigneeOptions
  ): UserResponse<StreamChatGenerics> => {
    return {
      id: assignee.value,
      name: assignee.label,
      firstName: assignee.user.firstName || '',
      lastName: assignee.user.lastName || '',
    } as unknown as UserResponse<StreamChatGenerics>
  }

  // Create a wrapper component for MessagingInput for threads in create channel context
  const CreateChannelThreadInput = useCallback((threadProps: any) => {
    return <MessagingInput {...threadProps} showChannelCreate={true} />
  }, [])

  // Create a wrapper component for our main MessagingInput in create channel context
  const CreateChannelMainInput = useCallback((inputProps: any) => {
    return <MessagingInput {...inputProps} showChannelCreate={true} />
  }, [])

  return (
    <div className="messaging-create-channel">
      <Window>
        <div className="relative h-full">
          <div className="pt-5 pl-5 xs:pr-3">
            {/* <header> */}
            <div className="flex justify-between">
              <div className="text-xl font-bold">New Message</div>
              <div
                onClick={() => {
                  setShowChannelCreate(false)
                  // Don't set showChannelPreview to false - let it stay true to always show content
                }}
                className="cursor-pointer"
              >
                <XButtonBackground />
              </div>
            </div>
            <div className="messaging-create-channel__left !items-center	">
              <div className="text-sm font-normal uppercase mt-5 mr-3">To: </div>
              <div className="users-input-container inline-flex">
                {
                  <div className="messaging-create-channel__users max-h-[39px] flex flex-wrap items-center">
                    {!!selectedUsers?.length &&
                      selectedUsers.map((user) => (
                        <div
                          className="messaging-create-channel__user bg-sky max-h-[39px] mt-2 mr-2 px-3 py-1 rounded-full flex items-center"
                          onClick={() => removeUser(user)}
                          key={user.id}
                        >
                          <div className="messaging-create-channel__user-text text-black-light text-base font-semibold font-primary mr-2">
                            {user.name}
                          </div>
                          <CrossIcon className={'fill-black-light h-[15px] w-[15px]'} />
                        </div>
                      ))}

                    {showSelect || selectedUsers.length === 0 ? (
                      <div style={{ width: '300px' }} className="mt-2">
                        <SelectField
                          placeholder="Select Team Members"
                          name={''}
                          options={assigneeOptions
                            .filter(
                              (assignee) =>
                                !selectedUsers.some((user) => user.id === assignee.value)
                            )
                            .map((assignee) => ({
                              label: assignee.label,
                              value: assignee.value,
                            }))}
                          onChange={(value) => {
                            const selectedAssignee = assigneeOptions.find(
                              (assignee) => assignee.value === value
                            )
                            if (selectedAssignee) {
                              addUser(convertAssigneeToStreamUser(selectedAssignee))
                            }
                            setShowSelect(true) // Keep the select open for adding more users
                          }}
                          isSearchable={true}
                        />
                      </div>
                    ) : (
                      <div className="mt-4 ml-5" onClick={() => setShowSelect(true)}>
                        <PlusIcon className="fill-primary h-[26px] w-[26px]" />
                      </div>
                    )}
                  </div>
                }
              </div>
            </div>
            {/* </header> */}
          </div>
          <div className="absolute bottom-0 w-full">
            <MessageInput
              overrideSubmitHandler={createChannelHandler}
              focus
              Input={CreateChannelMainInput}
            />
          </div>
        </div>
      </Window>
      <Thread Input={CreateChannelThreadInput} />
    </div>
  )
}

export default CreateChannel
